import { Link, useLocation } from 'react-router'
import {
  getStaticPageByPath,
  getStaticPageItemKey,
  getStaticPageBreadcrumbs,
  getStaticPages,
  type StaticPage as StaticPageData,
  type StaticPageItem,
  type StaticPageSection,
} from '@/lib/staticPages'
import { buildCounsellingWhatsAppUrl, siteConfig } from '@/lib/siteConfig'
import { BreadcrumbStructuredData, StaticPageStructuredData } from '@/lib/seo'
import { useCurrentTime } from '@/lib/hydration'
import { cn } from '@/lib/utils'
import { SiteFooter } from '@/components/layout/SiteFooter'
import { SiteHeader } from '@/components/layout/SiteHeader'
import { buttonClassName } from '@/components/ui/buttonStyles'
import { Aurora } from '@/components/design/Aurora'
import { FaqList } from '@/components/design/FaqList'
import { Reveal } from '@/components/design/Reveal'
import { Accent, SectionLabel } from '@/components/design/SectionLabel'
import { NotFound } from '@/pages/NotFound'
import {
  IconArrowRight,
  IconBook,
  IconCheck,
  IconChevronRight,
  IconMessageCircle,
  IconShieldCheck,
} from '@/components/Icons'

const CARD =
  'rounded-3xl border border-[var(--surface-border)] bg-[linear-gradient(180deg,#0f1216,#0b0d10)]'

function sectionAnchor(heading: string) {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

type LinkItem = Exclude<StaticPageItem, string>

function linkItems(section: StaticPageSection): LinkItem[] | null {
  const items = section.items ?? []
  if (items.length === 0) return null
  const links = items.filter((item): item is LinkItem => typeof item !== 'string')
  return links.length === items.length ? links : null
}

export function StaticPage() {
  const location = useLocation()
  const now = useCurrentTime()
  const page = getStaticPageByPath(location.pathname, now)

  if (!page) {
    return <NotFound />
  }

  const breadcrumbs = getStaticPageBreadcrumbs(page)
  const content = page.sections.filter((section) => section.tone !== 'notice')
  const notices = page.sections.filter((section) => section.tone === 'notice')
  const isArticle = page.kind === 'guide' && page.path !== '/guides'
  const relatedGuides = isArticle
    ? getStaticPages(now)
        .filter(
          (item) => item.kind === 'guide' && item.path !== '/guides' && item.path !== page.path,
        )
        .slice(0, 3)
    : []

  return (
    <>
      <StaticPageStructuredData
        title={page.title}
        description={page.metaDescription}
        path={page.path}
        schemaType={page.schemaType}
      />
      <BreadcrumbStructuredData items={breadcrumbs} />
      <SiteHeader />

      <main>
        <section className="relative overflow-hidden pb-14 pt-28 sm:pt-32">
          <Aurora tone="hero" />
          <div className="relative mx-auto grid max-w-[1100px] gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_320px] lg:items-end">
            <div>
              {breadcrumbs.length > 2 && (
                <nav aria-label="Breadcrumb" className="animate-rise mb-6">
                  <ol className="flex flex-wrap items-center gap-1.5 text-[13px] text-[var(--surface-muted)]">
                    {breadcrumbs.slice(0, -1).map((crumb) => (
                      <li key={crumb.url} className="inline-flex items-center gap-1.5">
                        <Link
                          to={crumb.url}
                          className="text-[var(--surface-muted)] no-underline transition-colors hover:text-[var(--text-strong)]"
                        >
                          {crumb.name}
                        </Link>
                        <IconChevronRight size={12} aria-hidden="true" />
                      </li>
                    ))}
                    <li
                      aria-current="page"
                      className="max-w-[46ch] truncate text-[var(--surface-fg)]"
                    >
                      {breadcrumbs[breadcrumbs.length - 1].name}
                    </li>
                  </ol>
                </nav>
              )}
              <SectionLabel className="animate-rise">{page.eyebrow}</SectionLabel>
              <h1 className="animate-rise-soft mt-7 max-w-[820px] text-balance font-display text-[clamp(32px,5vw,56px)] font-semibold leading-[1.04] tracking-[-0.045em] text-gradient-white [--rise-delay:60ms]">
                {page.h1}
              </h1>
              <p className="animate-rise mt-6 max-w-[760px] text-lg leading-relaxed text-[var(--surface-muted)] [--rise-delay:120ms]">
                {page.introAnswer}
              </p>
            </div>

            <aside
              aria-labelledby="admission-contact"
              className="animate-rise-soft rounded-3xl border border-[var(--surface-border)] bg-[rgba(13,15,18,0.85)] p-6 shadow-[var(--shadow-lg)] backdrop-blur [--rise-delay:160ms]"
            >
              <div className="mb-4 flex items-center gap-2">
                <IconMessageCircle
                  size={16}
                  aria-hidden="true"
                  className="text-[var(--color-brand-accent-readable)]"
                />
                <h2
                  id="admission-contact"
                  className="text-sm font-semibold text-[var(--text-strong)]"
                >
                  Talk to the admission desk
                </h2>
              </div>
              <div className="space-y-2 text-sm text-[var(--surface-muted)]">
                <p>
                  WhatsApp:{' '}
                  <a
                    href={buildCounsellingWhatsAppUrl(now)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--color-brand-accent-readable)] no-underline hover:underline"
                  >
                    {siteConfig.contact.phoneDisplay}
                  </a>
                </p>
                <p>
                  Email:{' '}
                  <a
                    href={`mailto:${siteConfig.contact.email}`}
                    className="text-[var(--color-brand-accent-readable)] no-underline hover:underline"
                  >
                    {siteConfig.contact.email}
                  </a>
                </p>
                <p>Location: {siteConfig.contact.location}</p>
              </div>
              <div className="mt-6 grid gap-2">
                <PageCta href={page.cta.href} label={page.cta.label} className="w-full" />
                {page.secondaryCtas?.map((cta) => (
                  <PageCta
                    key={`${cta.href}-${cta.label}`}
                    href={cta.href}
                    label={cta.label}
                    className="w-full"
                    variant="secondary"
                  />
                ))}
              </div>
            </aside>
          </div>
        </section>

        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-[1100px] px-4 sm:px-6">
            {isArticle ? (
              <ArticleBody sections={content} />
            ) : page.kind === 'roadmap' ? (
              <RoadmapBody sections={content} />
            ) : (
              <CardBody sections={content} />
            )}
            {notices.length > 0 && (
              <div className={cn('mt-8 grid gap-4', isArticle && 'lg:ml-[274px] lg:max-w-[720px]')}>
                {notices.map((section) => (
                  <Notice key={section.heading} section={section} />
                ))}
              </div>
            )}
          </div>
        </section>

        {relatedGuides.length > 0 && <RelatedGuides guides={relatedGuides} />}

        <section aria-labelledby="page-faq" className="py-12 sm:py-16">
          <div className="mx-auto grid max-w-[1100px] gap-10 px-4 sm:px-6 lg:grid-cols-[0.75fr_1.25fr]">
            <div>
              <SectionLabel>Student questions</SectionLabel>
              <h2
                id="page-faq"
                className="mt-6 font-display text-[clamp(28px,3.6vw,40px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--text-strong)]"
              >
                Frequently asked <Accent>questions</Accent>
              </h2>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-[var(--surface-muted)]">
                Still unsure? Ask on WhatsApp and the admission desk will reply.
              </p>
            </div>
            <FaqList items={page.faqs} name={`faq-${page.slug}`} />
          </div>
        </section>

        <section className="relative overflow-hidden pb-24 pt-8">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_55%_60%_at_50%_100%,rgba(0,229,255,0.14),transparent)]"
          />
          <div className="relative mx-auto max-w-[760px] px-4 text-center sm:px-6">
            <h2 className="font-display text-[clamp(28px,4vw,42px)] font-bold tracking-[-0.03em] text-[var(--text-strong)]">
              Need help choosing your <Accent>next step?</Accent>
            </h2>
            <p className="mt-4 text-[var(--surface-muted)]">
              Skillo can help you choose a course path based on your current skills, time, laptop
              access, and career goals.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <PageCta href={page.cta.href} label={page.cta.label} />
              {page.secondaryCtas?.map((cta) => (
                <PageCta
                  key={`${cta.href}-${cta.label}`}
                  href={cta.href}
                  label={cta.label}
                  variant="secondary"
                />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </>
  )
}

function SectionContent({
  section,
  large = false,
}: {
  section: StaticPageSection
  large?: boolean
}) {
  return (
    <>
      {section.body && (
        <p
          className={cn(
            'mt-3 leading-relaxed text-[var(--surface-muted)]',
            large ? 'text-base sm:text-[17px]' : 'text-sm sm:text-base',
          )}
        >
          {section.body}
        </p>
      )}
      {section.items && (
        <ul className="mt-5 grid gap-3">
          {section.items.map((item) => (
            <li
              key={getStaticPageItemKey(item)}
              className={cn(
                'flex items-start gap-3 leading-relaxed text-[var(--surface-fg)]',
                large ? 'text-[15px] sm:text-base' : 'text-sm',
              )}
            >
              <span
                aria-hidden="true"
                className="mt-[3px] flex size-[18px] shrink-0 items-center justify-center rounded-full bg-[var(--color-brand-accent)]/10 text-[var(--color-brand-accent-readable)]"
              >
                <IconCheck size={11} />
              </span>
              <StaticPageItemText item={item} />
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function ArticleBody({ sections }: { sections: StaticPageSection[] }) {
  return (
    <div className="grid gap-10 lg:grid-cols-[220px_minmax(0,720px)] lg:gap-[54px]">
      <nav aria-label="On this page" className="hidden lg:block">
        <div className="sticky top-28">
          <p className="font-code text-[11px] uppercase tracking-[0.2em] text-[var(--surface-muted)]">
            On this page
          </p>
          <ol className="mt-4 grid gap-1 border-l border-white/[0.08]">
            {sections.map((section) => (
              <li key={section.heading}>
                <a
                  href={`#${sectionAnchor(section.heading)}`}
                  className="-ml-px block border-l border-transparent py-1.5 pl-4 text-[13px] leading-snug text-[var(--surface-muted)] no-underline transition-colors hover:border-[var(--color-brand-accent)] hover:text-[var(--text-strong)]"
                >
                  {section.heading}
                </a>
              </li>
            ))}
          </ol>
        </div>
      </nav>
      <div className="min-w-0">
        {sections.map((section) => (
          <Reveal key={section.heading}>
            <section
              aria-labelledby={sectionAnchor(section.heading)}
              className="scroll-mt-28 border-t border-white/[0.07] py-9 first:border-t-0 first:pt-0"
            >
              <h2
                id={sectionAnchor(section.heading)}
                className="scroll-mt-28 font-display text-[clamp(22px,2.6vw,30px)] font-semibold leading-tight tracking-[-0.025em] text-[var(--text-strong)]"
              >
                {section.heading}
              </h2>
              <SectionContent section={section} large />
            </section>
          </Reveal>
        ))}
      </div>
    </div>
  )
}

function RoadmapBody({ sections }: { sections: StaticPageSection[] }) {
  return (
    <ol className="relative mx-auto max-w-[860px]">
      {sections.map((section, index) => (
        <li
          key={section.heading}
          className="relative grid grid-cols-[44px_1fr] gap-4 pb-6 sm:gap-6"
        >
          {index < sections.length - 1 && (
            <span
              aria-hidden="true"
              className="absolute bottom-0 left-[21px] top-11 w-px bg-gradient-to-b from-[var(--color-brand-accent)]/50 to-white/[0.06]"
            />
          )}
          <span className="relative z-10 flex size-11 items-center justify-center rounded-full border border-[var(--color-brand-accent)]/40 bg-[#0b0d10] font-code text-xs font-semibold text-[var(--color-brand-accent-readable)] shadow-[0_0_24px_-6px_rgba(0,229,255,0.6)]">
            {String(index + 1).padStart(2, '0')}
          </span>
          <Reveal>
            <article className={cn(CARD, 'p-6 sm:p-7')}>
              <h2 className="font-display text-xl font-semibold tracking-[-0.02em] text-[var(--text-strong)] sm:text-2xl">
                {section.heading}
              </h2>
              <SectionContent section={section} />
            </article>
          </Reveal>
        </li>
      ))}
    </ol>
  )
}

function CardBody({ sections }: { sections: StaticPageSection[] }) {
  return (
    <div className="gap-5 md:columns-2">
      {sections.map((section, index) => {
        const links = linkItems(section)
        return (
          <Reveal key={section.heading} className="mb-5 break-inside-avoid">
            <article className={cn(CARD, 'p-6 sm:p-8')}>
              <p className="font-code text-[11px] text-[var(--color-brand-accent-readable)]">
                {String(index + 1).padStart(2, '0')}
              </p>
              <h2 className="mt-3 font-display text-[22px] font-semibold tracking-[-0.02em] text-[var(--text-strong)] sm:text-2xl">
                {section.heading}
              </h2>
              {links ? (
                <>
                  {section.body && (
                    <p className="mt-3 text-sm leading-relaxed text-[var(--surface-muted)] sm:text-base">
                      {section.body}
                    </p>
                  )}
                  <ul className="mt-5 grid gap-2">
                    {links.map((item) => (
                      <li key={getStaticPageItemKey(item)}>
                        <LinkCard item={item} />
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <SectionContent section={section} />
              )}
            </article>
          </Reveal>
        )
      })}
    </div>
  )
}

function LinkCard({ item }: { item: LinkItem }) {
  const className =
    'group flex items-center justify-between gap-4 rounded-2xl border border-white/[0.07] bg-white/[0.02] px-4 py-3.5 text-sm font-medium text-[var(--surface-fg)] no-underline transition-[border-color,background-color,color] duration-300 hover:border-[var(--color-brand-accent)]/40 hover:bg-[var(--color-brand-accent)]/[0.05] hover:text-[var(--text-strong)]'
  const content = (
    <>
      <span className="flex items-center gap-3">
        <IconBook
          size={15}
          aria-hidden="true"
          className="shrink-0 text-[var(--color-brand-accent-readable)]"
        />
        {item.label}
      </span>
      <IconArrowRight
        size={14}
        aria-hidden="true"
        className="shrink-0 text-[var(--surface-muted)] transition-transform group-hover:translate-x-0.5 group-hover:text-[var(--color-brand-accent-readable)]"
      />
    </>
  )
  return item.external ? (
    <a href={item.href} target="_blank" rel="noopener noreferrer" className={className}>
      {content}
    </a>
  ) : (
    <Link to={item.href} className={className}>
      {content}
    </Link>
  )
}

function Notice({ section }: { section: StaticPageSection }) {
  return (
    <aside
      aria-label={section.heading}
      className="flex gap-3 rounded-2xl border border-[var(--marker)]/25 bg-[var(--marker)]/[0.05] p-5"
    >
      <IconShieldCheck
        size={18}
        aria-hidden="true"
        className="mt-0.5 shrink-0 text-[var(--marker)]"
      />
      <div className="text-sm leading-relaxed text-[var(--surface-muted)]">
        <h2 className="font-semibold text-[var(--text-strong)]">{section.heading}</h2>
        {section.body && <p className="mt-1">{section.body}</p>}
        {section.items && (
          <ul className="mt-2 grid gap-1.5">
            {section.items.map((item) => (
              <li key={getStaticPageItemKey(item)}>
                <StaticPageItemText item={item} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  )
}

function RelatedGuides({ guides }: { guides: StaticPageData[] }) {
  return (
    <section aria-labelledby="more-guides" className="pb-4">
      <div className="mx-auto max-w-[1100px] px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2
            id="more-guides"
            className="font-display text-[clamp(24px,3vw,32px)] font-semibold tracking-[-0.03em] text-[var(--text-strong)]"
          >
            Keep <Accent>reading</Accent>
          </h2>
          <Link
            to="/guides"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--color-brand-accent-readable)] no-underline transition-[gap] hover:gap-2.5"
          >
            All guides <IconArrowRight size={13} aria-hidden="true" />
          </Link>
        </div>
        <ul className="mt-6 grid gap-4 md:grid-cols-3">
          {guides.map((guide) => (
            <li key={guide.path}>
              <Link
                to={guide.path}
                className={cn(
                  CARD,
                  'group flex h-full flex-col p-6 no-underline transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[var(--color-brand-accent)]/35',
                )}
              >
                <span className="font-code text-[11px] uppercase tracking-[0.18em] text-[var(--color-brand-accent-readable)]">
                  Guide
                </span>
                <span className="mt-3 font-display text-lg font-semibold leading-snug text-[var(--text-strong)]">
                  {guide.h1}
                </span>
                <span className="mt-2 line-clamp-3 text-sm leading-relaxed text-[var(--surface-muted)]">
                  {guide.metaDescription}
                </span>
                <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold text-[var(--color-brand-accent-readable)]">
                  Read the guide
                  <IconArrowRight
                    size={13}
                    aria-hidden="true"
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

function StaticPageItemText({ item }: { item: StaticPageItem }) {
  if (typeof item === 'string') {
    return <span>{item}</span>
  }

  const className =
    'text-[var(--color-brand-accent-readable)] no-underline hover:underline underline-offset-4'

  if (item.external) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" className={className}>
        {item.label}
      </a>
    )
  }

  return (
    <Link to={item.href} className={className}>
      {item.label}
    </Link>
  )
}

function PageCta({
  href,
  label,
  className = '',
  variant = 'primary',
}: {
  href: string
  label: string
  className?: string
  variant?: 'primary' | 'secondary'
}) {
  const classes = cn(
    buttonClassName({ variant: variant === 'primary' ? 'accent' : 'secondary', size: 'lg' }),
    'group rounded-full px-6 text-sm',
    className,
  )

  if (href.startsWith('http')) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={classes}>
        {label}
        <IconArrowRight
          size={15}
          aria-hidden="true"
          className="transition-transform group-hover:translate-x-0.5"
        />
      </a>
    )
  }

  return (
    <Link to={href} className={classes}>
      {label}
      <IconArrowRight
        size={15}
        aria-hidden="true"
        className="transition-transform group-hover:translate-x-0.5"
      />
    </Link>
  )
}
